import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";
import { Loader2, Code, Wand2, AlertTriangle } from "lucide-react";

export interface PolicyFormData {
  name: string;
  command: string;
  commands: string[];
  permissive: boolean;
  using: string;
  withCheck: string;
  roles: string;
}

const COMMANDS = ["ALL", "SELECT", "INSERT", "UPDATE", "DELETE"];

// ── Condition Builder Presets ────────────────────────────────────────

interface ConditionPreset {
  id: string;
  label: string;
  description: string;
  buildUsing: (column: string) => string;
  buildCheck: (column: string) => string;
  needsColumn: boolean;
  defaultColumn: string;
}

const PRESETS: ConditionPreset[] = [
  {
    id: "owner",
    label: "Owner-based",
    description: "Users can only access their own rows",
    buildUsing: (col) => `app.uid() = ${col}`,
    buildCheck: (col) => `app.uid() = ${col}`,
    needsColumn: true,
    defaultColumn: "user_id",
  },
  {
    id: "org",
    // NOTE: auth.jwt() lives in the platform's Better Auth schema, which holds
    // every tenant's users and which project roles are correctly denied. There
    // is no app.jwt() to use instead, so this preset produces a policy Postgres
    // refuses with "permission denied for schema auth". Left as-is rather than
    // guessed at: it needs a project-facing claims helper to exist first.
    label: "Organization-based",
    description: "Users can access rows belonging to their organization",
    buildUsing: (col) => `(auth.jwt()->>'org_id')::text = ${col}::text`,
    buildCheck: (col) => `(auth.jwt()->>'org_id')::text = ${col}::text`,
    needsColumn: true,
    defaultColumn: "org_id",
  },
  {
    id: "tenant",
    label: "Tenant isolation",
    description: "Rows scoped to the current project/tenant",
    buildUsing: (col) => `${col} = current_setting('app.current_tenant', true)`,
    buildCheck: (col) => `${col} = current_setting('app.current_tenant', true)`,
    needsColumn: true,
    defaultColumn: "tenant_id",
  },
  {
    id: "public_read",
    label: "Public read",
    description: "Anyone can read all rows (use with SELECT command)",
    buildUsing: () => "true",
    buildCheck: () => "true",
    needsColumn: false,
    defaultColumn: "",
  },
  {
    id: "authenticated_only",
    label: "Authenticated only",
    description: "Only logged-in users can access rows",
    buildUsing: () => "app.uid() IS NOT NULL",
    buildCheck: () => "app.uid() IS NOT NULL",
    needsColumn: false,
    defaultColumn: "",
  },
  {
    id: "custom",
    label: "Custom SQL",
    description: "Write your own SQL expression",
    buildUsing: () => "",
    buildCheck: () => "",
    needsColumn: false,
    defaultColumn: "",
  },
];

export function PolicyForm({
  initial,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  initial?: Partial<PolicyFormData>;
  onSubmit: (data: PolicyFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}) {
  const initialCommands = initial?.commands
    || (initial?.command && initial.command !== "ALL" ? [initial.command] : ["ALL"]);

  const [name, setName] = useState(initial?.name || "");
  const [commands, setCommands] = useState<Set<string>>(new Set(initialCommands));
  const [permissive, setPermissive] = useState(initial?.permissive ?? true);
  const [roles, setRoles] = useState(initial?.roles || "public");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Condition builder state
  const isEditing = !!initial?.using || !!initial?.withCheck;
  const [preset, setPreset] = useState<string>(isEditing ? "custom" : "owner");
  const [column, setColumn] = useState(initial?.using?.match(/auth\.uid\(\)\s*=\s*(\w+)/)?.[1] || "user_id");
  // Whether the operator typed their own column. Presets carry a default
  // column, and switching preset must move the column with it: "Tenant
  // isolation" left holding the owner preset's user_id builds
  // `user_id = current_setting('app.current_tenant', true)`, which is a
  // policy that compares a user column against a tenant id. The old guard
  // (`!column`) could never fire, because column initialises to "user_id"
  // and is therefore never empty. A column the operator typed is theirs and
  // survives the switch.
  const [columnEdited, setColumnEdited] = useState(false);
  const [customUsing, setCustomUsing] = useState(initial?.using || "");
  const [customCheck, setCustomCheck] = useState(initial?.withCheck || "");
  const [showSql, setShowSql] = useState(isEditing);

  const activePreset = PRESETS.find((p) => p.id === preset)!;

  // Generate the actual SQL expressions
  const usingExpr = preset === "custom" ? customUsing : activePreset.buildUsing(column);
  const checkExpr = preset === "custom" ? customCheck : activePreset.buildCheck(column);

  function toggleCommand(cmd: string) {
    setCommands((prev) => {
      const next = new Set(prev);
      if (cmd === "ALL") {
        if (next.has("ALL")) next.delete("ALL"); else { next.clear(); next.add("ALL"); }
      } else {
        next.delete("ALL");
        if (next.has(cmd)) next.delete(cmd); else next.add(cmd);
        if (next.has("SELECT") && next.has("INSERT") && next.has("UPDATE") && next.has("DELETE")) { next.clear(); next.add("ALL"); }
      }
      return next;
    });
  }

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError("Policy name is required"); return; }
    if (commands.size === 0) { setError("Select at least one command"); return; }

    setSaving(true);
    try {
      const cmds = Array.from(commands);
      if (cmds.length === 1) {
        await onSubmit({ name, command: cmds[0], commands: cmds, permissive, using: usingExpr, withCheck: checkExpr, roles });
      } else {
        const groupId = Math.random().toString(36).slice(2, 8);
        for (const cmd of cmds) {
          await onSubmit({ name: `${name}_${cmd.toLowerCase()}_${groupId}`, command: cmd, commands: [cmd], permissive, using: usingExpr, withCheck: checkExpr, roles });
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 border border-border rounded-md p-4 bg-card">
      {/* Policy name */}
      <div className="space-y-1">
        <Label htmlFor="policy-name" className="text-sm">Policy Name</Label>
        <Input id="policy-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="policy_name" className="h-8 text-sm font-mono" />
      </div>

      {/* Command multi-select */}
      <div className="space-y-1">
        <Label className="text-sm">Commands</Label>
        <div className="flex gap-1.5">
          {COMMANDS.map((cmd) => (
            <button key={cmd} type="button" onClick={() => toggleCommand(cmd)}
              className={cn("px-3 py-1.5 rounded-md border text-xs font-medium transition-colors",
                commands.has(cmd) ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:border-foreground/30")} >
              {cmd}
            </button>
          ))}
        </div>
      </div>

      {/* Permissive toggle */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Permissive</p>
          <p className="text-xs text-muted-foreground">Permissive policies are OR'd together; restrictive are AND'd</p>
        </div>
        <Switch checked={permissive} onCheckedChange={setPermissive} />
      </div>

      {/* Roles */}
      <div className="space-y-1">
        <Label htmlFor="policy-roles" className="text-sm">Roles</Label>
        <Input id="policy-roles" value={roles} onChange={(e) => setRoles(e.target.value)} placeholder="authenticated" className="h-8 text-sm font-mono" />
        <p className="text-xs text-muted-foreground">
          Common roles: <code className="bg-muted px-1 rounded">authenticated</code>,{" "}
          <code className="bg-muted px-1 rounded">anon</code>,{" "}
          <code className="bg-muted px-1 rounded">service_role</code>,{" "}
          <code className="bg-muted px-1 rounded">public</code> (all).
        </p>
      </div>

      {/* Condition Builder */}
      <div className="space-y-2 pt-1">
        <div className="flex items-center justify-between">
          <Label className="text-sm flex items-center gap-1.5">
            <Wand2 className="h-3.5 w-3.5" />
            Condition
          </Label>
          <button onClick={() => setShowSql(!showSql)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Code className="h-3 w-3" />
            {showSql ? "Hide SQL" : "Show SQL"}
          </button>
        </div>

        {/* Preset selector */}
        <div className="grid grid-cols-3 gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setPreset(p.id);
                if (p.needsColumn && !columnEdited) setColumn(p.defaultColumn);
              }}
              className={cn(
                "rounded-md border px-2.5 py-2 text-left transition-colors",
                preset === p.id
                  ? "border-primary bg-primary/5"
                  : "border-border/50 hover:border-border",
              )}
            >
              <p className="text-xs font-medium">{p.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{p.description}</p>
            </button>
          ))}
        </div>

        {/* Column input (for presets that need it) */}
        {activePreset.needsColumn && (
          <div className="space-y-1">
            <Label htmlFor="policy-column" className="text-xs text-muted-foreground">Column to compare</Label>
            <Input
              id="policy-column"
              value={column}
              onChange={(e) => { setColumn(e.target.value); setColumnEdited(true); }}
              placeholder="user_id"
              className="h-7 text-xs font-mono"
            />
          </div>
        )}

        {/* Custom SQL (only for custom preset) */}
        {preset === "custom" && (
          <div className="space-y-2">
            <div className="space-y-1">
              <Label htmlFor="policy-using" className="text-xs text-muted-foreground">USING (read filter)</Label>
              <textarea
                id="policy-using"
                value={customUsing}
                onChange={(e) => setCustomUsing(e.target.value)}
                placeholder="e.g. app.uid() = user_id"
                rows={2}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="policy-with-check" className="text-xs text-muted-foreground">WITH CHECK (write filter)</Label>
              <textarea
                id="policy-with-check"
                value={customCheck}
                onChange={(e) => setCustomCheck(e.target.value)}
                placeholder="e.g. app.uid() = user_id"
                rows={2}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
              />
            </div>
          </div>
        )}

        {/* SQL Preview */}
        {showSql && preset !== "custom" && (
          <div className="rounded-md bg-[#0d1117] border border-border/20 p-3 space-y-1">
            {usingExpr && (
              <div>
                <span className="text-[10px] text-[#8b949e] uppercase">USING</span>
                <pre className="text-xs font-mono text-[#c9d1d9]">{usingExpr}</pre>
              </div>
            )}
            {checkExpr && checkExpr !== usingExpr && (
              <div>
                <span className="text-[10px] text-[#8b949e] uppercase">WITH CHECK</span>
                <pre className="text-xs font-mono text-[#c9d1d9]">{checkExpr}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gap analysis warnings */}
      {(() => {
        const warnings: string[] = [];
        const cmds = Array.from(commands);
        const hasInsert = cmds.includes("INSERT") || cmds.includes("ALL");
        const hasUpdate = cmds.includes("UPDATE") || cmds.includes("ALL");

        if (hasInsert && !checkExpr?.trim()) {
          warnings.push("INSERT policy without WITH CHECK allows unrestricted inserts.");
        }
        if (hasUpdate && !usingExpr?.trim()) {
          warnings.push("UPDATE policy without USING allows updating any row.");
        }
        if (
          (usingExpr?.includes("current_setting('app.current_tenant'") ||
           checkExpr?.includes("current_setting('app.current_tenant'")) &&
          preset !== "custom"
        ) {
          warnings.push("Tenant isolation policy references current_setting. Ensure the table has a tenant_id column.");
        }

        if (warnings.length === 0) return null;
        return (
          <div className="space-y-1.5">
            {warnings.map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md border border-amber-300/30 bg-amber-50/80 dark:bg-amber-500/10 dark:border-amber-500/20 px-3 py-2"
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-700 dark:text-amber-400">{w}</p>
              </div>
            ))}
          </div>
        );
      })()}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={handleSubmit} disabled={saving || commands.size === 0}>
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
